import React, { useState, useEffect, useCallback } from 'react'
import { api, formatCurrencyFromCents, formatDate } from '../../utils/api'
import { monthRange, thisMonth, csvFileName } from '../../utils/storeCreditLedger'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Store credit ledger for accounting: every store credit payout issued, by month.
// Prism owns the figures, the totals and the CSV. This page picks the month,
// shows what Prism sent and offers the download. It does no money maths.
function StoreCreditLedger() {
  const [month, setMonth] = useState(thisMonth())
  const [ledger, setLedger] = useState(null)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const query = useCallback(() => {
    const { from, to } = monthRange(month)
    return `date_from=${from}&date_to=${to}`
  }, [month])

  useEffect(() => {
    if (!month) return undefined
    let cancelled = false
    setLedger(null)
    setError(null)
    api.get(`/api/deals/store-credit-ledger?${query()}`)
      .then(data => { if (!cancelled) setLedger(data) })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not read the ledger') })
    return () => { cancelled = true }
  }, [month, query])

  async function downloadCsv() {
    setDownloading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/api/deals/store-credit-ledger/csv?${query()}`, { credentials: 'include' })
      if (!response.ok) throw new Error(`The download failed (${response.status})`)
      const url = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = csvFileName(month)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e.message || 'The download failed')
    } finally {
      setDownloading(false)
    }
  }

  const totals = ledger?.totals
  const rows = ledger?.rows || []

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-4)' }}>
        <label htmlFor="ledger-month" style={{ fontWeight: 600 }}>Month</label>
        <input
          id="ledger-month"
          type="month"
          value={month}
          max={thisMonth()}
          onChange={e => setMonth(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={downloadCsv} disabled={downloading || !ledger}>
          {downloading ? 'Preparing...' : 'Download CSV'}
        </button>
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 'var(--space-3)', color: '#b91c1c' }}>{error}</div>
      )}

      {totals && (
        <div className="stats-grid" style={{ marginTop: 'var(--space-4)' }}>
          <div className="stat-card">
            <div className="stat-value">{totals.count}</div>
            <div className="stat-label">Issued</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrencyFromCents(totals.cash_cents)}</div>
            <div className="stat-label">Cash value</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrencyFromCents(totals.credit_cents)}</div>
            <div className="stat-label">Credit value</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrencyFromCents(totals.uplift_cents)}</div>
            <div className="stat-label">Uplift</div>
          </div>
        </div>
      )}

      {!error && <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        {!ledger ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--muted)' }}>
            <h3>No store credit issued</h3>
            <p>Nothing was issued in this month.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Issued</th>
                <th>Quote</th>
                <th>Seller</th>
                <th>Items</th>
                <th style={{ textAlign: 'right' }}>Cash value</th>
                <th style={{ textAlign: 'right' }}>Credit value</th>
                <th style={{ textAlign: 'right' }}>Uplift</th>
                <th>Shopify transaction</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.quote_payment_id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.issued_at)}</td>
                  <td>{row.reference_code}</td>
                  <td>
                    <div>{row.seller_name || '-'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{row.seller_email || 'No email'}</div>
                  </td>
                  <td>{row.item_names.join(', ') || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrencyFromCents(row.cash_cents)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrencyFromCents(row.credit_cents)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrencyFromCents(row.uplift_cents)}
                    {row.uplift_bps != null && (
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{row.uplift_bps / 100}%</div>
                    )}
                  </td>
                  <td style={{ fontSize: 12, wordBreak: 'break-all' }}>{row.shopify_transaction_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>}
    </>
  )
}

export default StoreCreditLedger
